// src/ecrans/accueil/BlocsCote.jsx — les blocs de l'Accueil NEST (maquette
// v819) autour de « À décider » : « Voyageurs qui attendent », la ligne de
// l'opérateur, « Mes tâches », « L'argent du mois ». LECTURE SEULE.
//
//   · Voyageurs : `ecran_conversations_lire` { filtre: en_attente } ;
//     « Répondre » ouvre le fil (Voyageurs › Messages), comme avant.
//   · Opérateur : `operateur_fait_list`, ses gestes faits SEUL aujourd'hui.
//   · Mes tâches : `ecran_taches_lire` { vue: semaine }. Ni case à cocher ni
//     « Ajouter » ici : les écritures viendront au lot 3 ; une case qui ne
//     coche rien mentirait. Toucher une tâche ouvre Opérations › Tâches.
//   · L'argent du mois : chemin d'argent, AFFICHAGE SEUL (lot 4, réponses
//     du 05/11, D-62) ; aucun calcul ni aucune lecture d'argent ici.
//     Détail au-dessus de `TUILES_ARGENT`.

import { C, EtatLecture, mot } from "../nest";
import { nombreDe } from "../portes";
import { ICONES } from "./accueil-style";
import { LigneDecision } from "./BlocADecider";
import CartePliee, { Carte } from "./CartePliee";
import { ligneFil, tachesAFaire } from "./accueil-calculs";

const initiales = (nom) =>
  String(nom || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((m) => m.charAt(0).toUpperCase())
    .join("") || "?";

/** Le fil qui attend depuis le plus longtemps (ou le premier, si l'attente n'est pas dite). */
function plusAncien(fils) {
  let r = null;
  for (const f of fils) if (!r || (Number(f.attente_depuis_jours) || 0) > (Number(r.attente_depuis_jours) || 0)) r = f;
  return r;
}

export function BlocVoyageurs({ messages, demandesADecider, onRelire, onOuvrirFil, onMicro, plie }) {
  const fils = messages.etat === "ok" ? messages.data.fils || [] : [];
  const n = nombreDe(messages, (d) => (d.fils || []).length);
  const demandes =
    Number.isInteger(demandesADecider) && demandesADecider > 0
      ? ` · ${demandesADecider} ${mot(demandesADecider, "demande est", "demandes sont")} dans « À décider »`
      : "";
  const sous =
    n === "…" ? "On lit…" : n === 0 ? "Aucun voyageur n'attend de réponse." : `${n} ${mot(n, "réponse à donner", "réponses à donner")}`;
  const vieux = plusAncien(fils);
  const resume = (
    <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
      {fils.length ? (
        <span className="nacc-avs" aria-hidden="true">
          {fils.slice(0, 3).map((f) => (
            <span key={f.thread_id} className="nacc-av">{initiales(f.voyageur)}</span>
          ))}
        </span>
      ) : null}
      <span className="nacc-tx">
        <b>{sous}</b>
        {vieux ? (
          <span>
            {vieux.voyageur || "Un voyageur"}{" "}
            {Number(vieux.attente_depuis_jours) > 0 ? "attend depuis le plus longtemps" : "attend une réponse"}
          </span>
        ) : null}
      </span>
    </span>
  );
  return (
    <CartePliee plie={plie} titre="Voyageurs qui attendent" ic="msg" resume={resume} testId="accueil-voyageurs">
      {(onReplier) => (
        <Carte titre="Voyageurs qui attendent" sous={sous + demandes} onReplier={onReplier} className="nacc-voy" testId="accueil-voyageurs">
          <EtatLecture lecture={messages.etat === "echec" ? messages : null} quoi="Messages" onRelire={onRelire} />
          <div className="nacc-list">
            {fils.map((f) => {
              const l = ligneFil(f);
              const attente = Number(f.attente_depuis_jours) > 0 ? `depuis ${f.attente_depuis_jours} j` : null;
              return (
                <LigneDecision
                  key={l.cle}
                  testId={`accueil-fil-${f.thread_id}`}
                  l={{
                    cle: l.cle,
                    ic: "msg",
                    titre: l.titre,
                    sousTitre: [l.extrait ? `« ${l.extrait} »` : null, f.logement ? f.logement.nom : null].filter(Boolean).join(" · "),
                    quand: attente,
                  }}
                  libelle="Répondre"
                  onTraiter={() => onOuvrirFil && onOuvrirFil(f.thread_id)}
                  onMicro={onMicro}
                />
              );
            })}
          </div>
        </Carte>
      )}
    </CartePliee>
  );
}

const jourParis = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Paris" }).format(d);
};

/** Les gestes que l'opérateur a faits SEUL aujourd'hui (porte `operateur_fait_list`). */
export function gestesSeulAujourdhui(data, today) {
  return ((data && data.gestes) || []).filter((g) => g && g.seul === true && jourParis(g.created_at) === today).length;
}

export function LigneOperateur({ fait, today, onOuvrir }) {
  const n = nombreDe(fait, (d) => gestesSeulAujourdhui(d, today));
  const texte =
    n === "…"
      ? "L'opérateur · on lit ce qu'il a fait…"
      : n === "—"
        ? "L'opérateur · ce qu'il a fait seul n'a pas pu être lu"
        : n === 0
          ? "L'opérateur n'a rien fait seul aujourd'hui"
          : `L'opérateur a fait ${n} ${mot(n, "chose", "choses")} seul aujourd'hui`;
  return (
    <button type="button" className="nacc-opl" onClick={onOuvrir} data-testid="accueil-operateur">
      <span className="nacc-lot" aria-hidden="true">
        <svg viewBox="0 0 24 24">{ICONES.feuille}</svg>
      </span>
      <b>{texte}</b>
      <span className="nacc-opgo">Ouvrir</span>
    </button>
  );
}

const MAX_TACHES = 6;

export function BlocTaches({ taches, onRelire, onAller, plie }) {
  const liste = taches.etat === "ok" ? tachesAFaire(taches.data) : [];
  const n = nombreDe(taches, (d) => tachesAFaire(d).length);
  const retard = liste.filter((t) => t.retard).length;
  const sous = n === "…" ? "On lit…" : `${n} à faire${retard ? ` · ${retard} en retard` : ""}`;
  const ouvrir = () => onAller && onAller("mes-taches");
  const resume = (
    <>
      <span className="nacc-pli-big">
        <b>{n}</b>
        <span>
          à faire{retard ? <> · <i style={{ fontStyle: "normal", color: C.aub, fontWeight: 700 }}>{retard} en retard</i></> : null}
        </span>
      </span>
      {liste[0] ? <span className="nacc-pli-next">{liste[0].titre}</span> : null}
    </>
  );
  return (
    <CartePliee plie={plie} titre="Mes tâches" ic="tache" resume={resume} testId="accueil-taches">
      {(onReplier) => (
        <Carte titre="Mes tâches" sous={sous} onReplier={onReplier} testId="accueil-taches">
          <EtatLecture lecture={taches.etat === "echec" ? taches : null} quoi="Tâches" onRelire={onRelire} />
          {taches.etat === "ok" && !liste.length ? <p className="nacc-vide">Aucune tâche à faire.</p> : null}
          {liste.slice(0, MAX_TACHES).map((t) => (
            <button key={t.id} type="button" className={`nacc-tk${t.retard ? " r" : ""}`} onClick={ouvrir}>
              <b>{t.titre}</b>
              <small>
                {[t.logement ? t.logement.nom : null, t.echeance ? `pour ${t.echeance}` : "sans date", t.retard ? "en retard" : null]
                  .filter(Boolean)
                  .join(" · ")}
              </small>
            </button>
          ))}
          {liste.length > MAX_TACHES ? (
            <button type="button" className="nacc-plus" onClick={ouvrir}>
              Voir les {liste.length} tâches ›
            </button>
          ) : null}
        </Carte>
      )}
    </CartePliee>
  );
}

/**
 * « L'argent du mois » (P-4.36, D-62 ; maquette v819 : À facturer, En retard,
 * Paie des agents, Taxe de séjour du direct). Réponses du 05/11 (D-62) :
 *   · « En retard » : « — ». L'appli ne sait pas encore dire qu'une facture
 *     est en retard ; on ne calcule rien, on n'invente rien (noyau 10).
 *   · « Paie des agents » : RETIRÉE de la carte (ni tuile, ni résumé).
 *   · Aucun montant de réservation n'est affiché dans cette carte.
 *   · « Taxe de séjour du direct » : seulement celle que la conciergerie
 *     reverse elle-même, pour les logements qu'elle loue. Rien pour les
 *     logements des propriétaires (décision en attente : pas de ligne, pas
 *     de calcul).
 * Le « à facturer » existe déjà dans Argent › Facturation (par logement) ; le
 * brancher ici serait un lot d'argent séparé. La taxe collectée n'a pas de
 * porte d'écran. Chaque tuile dit donc « — », jamais un faux 0.
 * Le jour où une porte les donnera, la règle des chiffres (décision 76)
 * s'appliquera : tout suivi → le nombre, zéro compris ; une partie → « sur N
 * logements suivis » ; rien → « — ».
 */
export const TUILES_ARGENT = Object.freeze([
  { cle: "facturer", titre: "À facturer", detail: "pas encore lu ici" },
  { cle: "retard", titre: "En retard", detail: "l'appli ne le sait pas encore" },
  { cle: "taxe", titre: "Taxe de séjour du direct", detail: "logements loués par la conciergerie · pas encore lue ici" },
]);

export function BlocArgent({ moisLib, plie }) {
  const sous = `${moisLib} · pas encore lu ici`;
  const resume = (
    <>
      <span className="nacc-pli-big">
        <b>—</b>
        <span>à facturer ce mois</span>
      </span>
      <span style={{ fontSize: 12.5, color: "#BFD8CF" }}>pas encore lu ici</span>
    </>
  );
  return (
    <CartePliee plie={plie} titre="L'argent du mois" ic={null} resume={resume} sombre testId="accueil-argent">
      {(onReplier) => (
        <Carte titre="L'argent du mois" sous={sous} onReplier={onReplier} className="nacc-arg" testId="accueil-argent">
          <div className="nacc-tiles">
            {TUILES_ARGENT.map((t) => (
              <div key={t.cle} className="nacc-t" data-testid="accueil-argent-tuile" data-cle={t.cle}>
                <small>{t.titre}</small>
                <b>—</b>
                <span>{t.detail}</span>
              </div>
            ))}
          </div>
        </Carte>
      )}
    </CartePliee>
  );
}
